const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

async function apiRequest(method: string, path: string, token: string, body?: any) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}

export async function initializeUser(token: string, displayName: string) {
  return apiRequest('POST', '/api/initialize_user', token, { display_name: displayName });
}

export async function getMyProfile(token: string) {
  return apiRequest('GET', '/api/profile/me', token);
}

export async function updateMyProfile(token: string, updates: any) {
  return apiRequest('PUT', '/api/profile/me', token, updates);
}

// Health Data
export async function logVitals(token: string, vitals: any) {
  return apiRequest('POST', '/api/health/vitals', token, vitals);
}

export async function getVitals(token: string, days = 30) {
  return apiRequest('GET', `/api/health/vitals?days=${days}`, token);
}

export async function logSymptoms(token: string, symptom: any) {
  return apiRequest('POST', '/api/health/symptoms', token, symptom);
}

export async function getHealthProfile(token: string) {
  return apiRequest('GET', '/api/health/profile', token);
}

export async function updateHealthProfile(token: string, profile: any) {
  return apiRequest('PUT', '/api/health/profile', token, profile);
}

export async function importAppleHealth(token: string, file: File, since?: string, until?: string) {
  const formData = new FormData();
  formData.append('file', file);
  const params = new URLSearchParams();
  if (since) params.set('since', since);
  if (until) params.set('until', until);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_URL}/api/health/import/apple_health${qs}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Import failed');
  return data;
}

// Analysis
export async function getDailySummary(token: string) {
  return apiRequest('GET', '/api/analysis/daily_summary', token);
}

export async function askHealthQuestion(token: string, question: string) {
  return apiRequest('POST', '/api/analysis/ask', token, { question });
}

export async function getHealthTrends(token: string) {
  return apiRequest('GET', '/api/analysis/trends', token);
}

export async function getHealthScore(token: string) {
  return apiRequest('GET', '/api/analysis/health_score', token);
}

export async function getStreaks(token: string) {
  return apiRequest('GET', '/api/analysis/streaks', token);
}

export async function getCorrelations(token: string) {
  return apiRequest('GET', '/api/analysis/correlations', token);
}

export async function getWeeklyDigest(token: string) {
  return apiRequest('GET', '/api/analysis/weekly_digest', token);
}

export async function getPerformanceWindow(token: string) {
  return apiRequest('GET', '/api/analysis/performance_window', token);
}

export async function logInvestmentDecision(token: string, decision: {
  description: string; outcome: string; pnl_pct?: number; energy_rating?: number; date?: string;
}) {
  return apiRequest('POST', '/api/ingest/investment_decision', token, decision);
}

export async function getSupplementAudit(token: string) {
  return apiRequest('GET', '/api/health/supplement_audit', token);
}

// Medications
export async function getMedications(token: string) {
  return apiRequest('GET', '/api/medications', token);
}

export async function addMedication(token: string, med: any) {
  return apiRequest('POST', '/api/medications', token, med);
}

export async function updateMedication(token: string, id: string, updates: any) {
  return apiRequest('PUT', `/api/medications/${id}`, token, updates);
}

export async function deleteMedication(token: string, id: string) {
  return apiRequest('DELETE', `/api/medications/${id}`, token);
}

export async function markMedicationTaken(token: string, id: string) {
  return apiRequest('POST', `/api/medications/${id}/taken`, token);
}

// Ingest / Connect
export async function getIngestToken(token: string) {
  return apiRequest('GET', '/api/ingest/token', token);
}

export async function regenerateIngestToken(token: string) {
  return apiRequest('POST', '/api/ingest/token/regenerate', token);
}

export async function submitJournal(token: string, text: string) {
  return apiRequest('POST', '/api/ingest/journal', token, { text, source: 'web' });
}

export async function getSubjectiveLogs(token: string, days = 30) {
  return apiRequest('GET', `/api/ingest/subjective?days=${days}`, token);
}

// Community
export async function getPersonas(token: string) {
  return apiRequest('GET', '/api/community/personas', token);
}

export async function askPersona(token: string, personaId: string, question: string, threadId?: string) {
  return apiRequest('POST', '/api/community/ask_persona', token, { persona_id: personaId, question, thread_id: threadId });
}

export async function getThreads(token: string) {
  return apiRequest('GET', '/api/community/threads', token);
}
